<template>
  <fin-chart-list class="market-wrapper-chart-list"
                  :items="items"
                  @input="chartsLoaded"
                  @selectedItem="selectedChartChange"
                  :no-preview="$vuetify.breakpoint.xsOnly"
  />
</template>

<script>
import FinChartList from "@/components/common/Chart2/PerformanceCharts/ChartList"
import FinChartBuilder from "@/components/common/Chart2/FinChartBuilder"
import {indexSubscription} from "@/js/finSubscriptions"
import AmplitudePush from "@/mixins/AmplitudePush"

const {graphqlOperation} = require('@/plugins/finAmplifyModules')

const queryDetail = `
  query($id:ID!){
    getIndex(id:$id){
        id
        mainListing{
          timeSeries {
            x:timestamp
            y:close
          }
        }
        realtime: timeSeriesRT {
          x:timestamp
          y:price
        }
    }
  }
`
const groups = [
  {
    title: "Aktienmärkte Deutschland",
    items: ['DE0008469008', 'DE0007203275', 'DE0008467416', 'DE0009653386'],
    tradingPlatform: FinChartBuilder.tradingPlatforms.citibank
  },
  {
    title: "Aktienmärkte International",
    items: ['US6311011026', 'US2605661048', 'JP9010C00002', 'US78378X1072'],
    tradingPlatform: FinChartBuilder.tradingPlatforms.citibank
  },
  {
    title: "Rohstoffe",
    items: ['XC0009655157', 'XC0009665545', 'XC0009653103'],
    tradingPlatform: FinChartBuilder.tradingPlatforms.infront
  },
]

const queryList = `{
   ${groups.map(({items}) =>
  items.map((isin, index) =>
    `${isin} : getIndex(id:"${isin}"){
                id
                name
                currency
                ${index >= 0 ? `
                  mainListing{
                    preview: timeSeries1Y {
                      x: timestamp
                      y: close
                    }
                  }
                  realtime: timeSeriesRT {
                    x:timestamp
                    y:price
                  }
                ` : ''}
              }`
  )
)}
}`

export default {
  name: 'fin-market-wrapper',
  mixins: [AmplitudePush],
  components: {
    FinChartList,
  },
  data() {
    return {
      charts: null,

    }
  },
  computed: {
    items() {
      return {
        load: graphqlOperation(queryList),
        adapt: this.adapter,
      }
    }
  },
  methods: {
    chartsLoaded(charts) {
      this.charts = charts
    },
    selectedChartChange(selCard) {
      this.pushToAmplitude({
        'event': 'fincraft_finhub_markets_smallcard',
        'step': 'fincraft_finhub_markets_select',
        'step_choice': selCard ? this.charts.findIndex(chart => chart.id === selCard.id) : '',
        'placement': 'finhub_markets',
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
    },
    adapter(data) {
      return groups.map((group, index) => {
        const item = data[group.items[0]]
        const currency = item.currency === 'EUR' ? '€' : '' //TODO improve but already better than static
        const preview = [...item.mainListing.preview, ...(item.realtime || [])]
        const previewData = FinChartBuilder.mapTimeSeriesPoint(preview, 'y', 'x')
        const previewChart = FinChartBuilder.createCardSelectorChart(item.name, previewData, 'small', this.$vuetify.breakpoint,
          true, currency)

        //helper just in case a subscription arrives to the mini chart while loading the big
        let lastpoint = null

        previewChart.subscriptions = [
          {
            subscription: graphqlOperation(indexSubscription, {id: item.id}),
            trigger({data}, {chart}) {
              const {price: y, timestamp, id} = data.onCreateRealTimePrice
              if (id !== item.id) {
                //check if this happens and how
                return
              }
              const x = (new Date(timestamp)).getTime()

              if (chart.series?.length) {
                lastpoint = [x, y]
                chart.series[0].addPoint(lastpoint, true, false)
                previewData.push(lastpoint)
                const {min} = chart.xAxis[0].getExtremes()
                chart.xAxis[0].setExtremes(min, x, true, true, {trigger: 'addPoint'})
              }
            }
          }
        ]

        return {
          id: index,
          title: `${group.title} (${item.name})`,
          tradingPlatform: group.tradingPlatform.id,
          titleDetail: group.title,
          onDetail: {
            userChange: (index) => {
              this.pushToAmplitude({
                'event': 'fincraft_finhub_markets_dropdown',
                'step': 'fincraft_finhub_markets_select',
                'step_choice': group.title + '_' + index,
                'placement': 'finhub_markets',
                'vue_breakpoint': this.$vuetify.breakpoint.name
              })
            }
          },
          chart: group.items.map((isin, itemindex) => {
            const item = data[isin]
            return {
              title: item.name,
              id: item.id,
              chart: {
                async load() {
                  return graphqlOperation(queryDetail, {id: item.id})
                },
                key: 'marketdataoverview_' + item.id,
                adapt: ({getIndex}) => {
                  const timeseries = [...getIndex.mainListing.timeSeries, ...getIndex.realtime]
                  const detailData = FinChartBuilder.mapTimeSeriesPoint(timeseries, 'y', 'x')

                  if (itemindex === 0 && lastpoint) {
                    const checklLast = detailData[detailData.length - 1]
                    if (lastpoint[0] > checklLast[0]) {
                      //subscription arrived while loading
                      detailData.push(lastpoint)
                    }
                  }

                  const chart = FinChartBuilder.createCardSelectorChart(item.name, detailData, 'big', this.$vuetify.breakpoint,
                    group.tradingPlatform, currency, null, false, false, false, 5)
                  chart.subscriptions = [
                    {
                      subscription: graphqlOperation(indexSubscription, {id: item.id}),
                      trigger({data}, {chart}) {
                        const {price: y, timestamp, id} = data.onCreateRealTimePrice
                        if (id !== item.id) {
                          //check if this happens and how
                          return
                        }
                        const x = (new Date(timestamp)).getTime()
                        if (chart.series?.length) {
                          const {selected} = chart.rangeSelector || {}
                          chart.series[0].addPoint([x, y], false, false)
                          const {min} = chart.xAxis[0].getExtremes()
                          chart.xAxis[0].setExtremes(min, x, true, true, {trigger: 'addPoint'})
                          chart.rangeSelector.clickButton(selected)
                        }
                      }
                    }
                  ]
                  return chart
                },
                init: itemindex === 0 ? FinChartBuilder.createCardSelectorChart(item.name, previewData, 'big', this.$vuetify.breakpoint,
                  group.tradingPlatform, currency, null, false, false, false, 5) : null

              }
            }
          }),
          previewChart
        }
      })
    }

  },
}
</script>


<style lang="scss" scoped>

</style>
